import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Box,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ApartmentIcon from '@mui/icons-material/Apartment';
import BadgeIcon from '@mui/icons-material/Badge';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GroupsIcon from '@mui/icons-material/Groups';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PeopleIcon from '@mui/icons-material/People';
import PersonPinIcon from '@mui/icons-material/PersonPin';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import WorkIcon from '@mui/icons-material/Work';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import HandymanIcon from '@mui/icons-material/Handyman';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';

export const drawerWidth = 280;
export const collapsedDrawerWidth = 88;

const menuSections = [
  {
    title: 'Cadastros',
    items: [
      { text: 'Empresas', path: '/empresas', roles: ['admin', 'sindico'], icon: <ApartmentIcon fontSize="small" /> },
      { text: 'Pessoas', path: '/pessoas', roles: ['admin', 'sindico', 'portaria'], icon: <PeopleIcon fontSize="small" /> },
      { text: 'Imóveis', path: '/imoveis', roles: ['admin', 'sindico'], icon: <BadgeIcon fontSize="small" /> },
      { text: 'Veículos', path: '/veiculos', roles: ['admin', 'sindico', 'portaria'], icon: <DirectionsCarIcon fontSize="small" /> },
      { text: 'Acessos Liberados', path: '/acessos-liberados', roles: ['admin', 'sindico', 'portaria'], icon: <VerifiedUserIcon fontSize="small" /> },
      { text: 'Usuários', path: '/usuarios', roles: ['admin'], icon: <SupervisorAccountIcon fontSize="small" /> },
      { text: 'Moradores', path: '/moradores', roles: ['admin', 'sindico', 'portaria'], icon: <GroupsIcon fontSize="small" /> },
      { text: 'Proprietários', path: '/proprietarios', roles: ['admin', 'sindico'], icon: <PersonPinIcon fontSize="small" /> },
      { text: 'Funcionários', path: '/funcionarios', roles: ['admin', 'sindico'], icon: <WorkIcon fontSize="small" /> },
      { text: 'Manutenção', path: '/cadastros-manutencao', roles: ['admin', 'sindico'], icon: <HandymanIcon fontSize="small" /> },
      { text: 'Importar cadastros', path: '/importacoes', roles: ['admin', 'sindico'], icon: <UploadFileIcon fontSize="small" /> }
    ]
  },
  {
    title: 'Operação',
    items: [
      { text: 'Painel', path: '/dashboard', roles: ['admin', 'sindico', 'portaria', 'padrao'], icon: <DashboardIcon fontSize="small" /> },
      { text: 'Visitantes', path: '/visitantes', roles: ['admin', 'sindico', 'padrao'], icon: <PeopleIcon fontSize="small" /> },
      { text: 'Acessos', path: '/acessos', roles: ['admin', 'sindico', 'portaria'], icon: <AccessTimeIcon fontSize="small" /> },
      { text: 'Encomendas', path: '/encomendas', roles: ['admin', 'sindico', 'portaria'], icon: <LocalShippingIcon fontSize="small" /> },
      { text: 'Consumo Água', path: '/consumo-agua', roles: ['admin', 'sindico', 'portaria'], icon: <WaterDropIcon fontSize="small" /> },
      { text: 'Manutenção', path: '/manutencoes', roles: ['admin', 'sindico'], icon: <HandymanIcon fontSize="small" /> },
      { text: 'Ponto', path: '/ponto', roles: ['admin', 'sindico', 'portaria'], icon: <VpnKeyIcon fontSize="small" /> }
    ]
  }
];

const getUserFirstName = (email) => {
  if (!email) return '';
  return email.split('@')[0].split('.')[0];
};

const desktopCollapsedStorageKey = 'sidebar.desktopCollapsed';

const SidebarMenu = ({ user, logout, mobileOpen, onMobileToggle }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const [desktopCollapsed, setDesktopCollapsed] = React.useState(() => {
    const storedValue = localStorage.getItem(desktopCollapsedStorageKey);
    return storedValue === 'true';
  });
  const [openSections, setOpenSections] = React.useState({
    Cadastros: true,
    Operação: true
  });

  React.useEffect(() => {
    localStorage.setItem(desktopCollapsedStorageKey, String(desktopCollapsed));
  }, [desktopCollapsed]);

  const isActivePath = (path) => location.pathname === path;
  const drawerWidthValue = desktopCollapsed ? collapsedDrawerWidth : drawerWidth;

  const toggleSection = (title) => {
    setOpenSections((prev) => ({
      ...prev,
      [title]: !prev[title]
    }));
  };

  const visibleSections = menuSections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => user?.tipo_usuario && item.roles.includes(user.tipo_usuario))
    }))
    .filter((section) => section.items.length > 0);

  const itemButtonSx = {
    borderRadius: 2,
    mx: 1,
    px: desktopCollapsed ? 1.5 : 2,
    py: 1.1,
    justifyContent: desktopCollapsed ? 'center' : 'flex-start',
    '&.Mui-selected': {
      bgcolor: 'primary.main',
      color: 'primary.contrastText',
      '&:hover': { bgcolor: 'primary.dark' }
    },
    '&.Mui-selected .MuiListItemText-primary': {
      fontWeight: 700
    }
  };

  const scrollAreaSx = {
    flexGrow: 1,
    overflowY: 'auto',
    py: 1,
    scrollbarWidth: 'thin',
    scrollbarColor: `${alpha(theme.palette.grey[400], 0.8)} transparent`,
    '&::-webkit-scrollbar': {
      width: 10
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent'
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: alpha(theme.palette.grey[400], 0.7),
      borderRadius: 8,
      border: `2px solid ${alpha(theme.palette.background.paper, 0.85)}`
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: alpha(theme.palette.grey[500], 0.8)
    }
  };

  const renderNavItem = (item) => (
    <Tooltip
      key={item.text}
      title={desktopCollapsed ? item.text : ''}
      placement="right"
      disableHoverListener={!desktopCollapsed}
      disableFocusListener={!desktopCollapsed}
      disableTouchListener={!desktopCollapsed}
    >
      <ListItem disablePadding sx={{ mb: 0.5 }}>
        <ListItemButton
          component={Link}
          to={item.path}
          selected={isActivePath(item.path)}
          onClick={isMobile ? onMobileToggle : undefined}
          sx={itemButtonSx}
        >
          <ListItemIcon
            sx={{
              minWidth: desktopCollapsed ? 0 : 36,
              mr: desktopCollapsed ? 0 : 1,
              color: 'inherit'
            }}
          >
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={item.text}
            sx={{
              display: desktopCollapsed ? 'none' : 'block',
              whiteSpace: 'nowrap'
            }}
          />
        </ListItemButton>
      </ListItem>
    </Tooltip>
  );

  const drawerContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: `linear-gradient(180deg, ${alpha(theme.palette.primary.dark, 0.08)} 0%, ${theme.palette.background.paper} 22%)`
      }}
    >
      <Toolbar
        sx={{
          px: desktopCollapsed ? 1.25 : 3,
          minHeight: 92,
          alignItems: 'flex-end',
          justifyContent: desktopCollapsed ? 'center' : 'space-between'
        }}
      >
        <Box sx={{ display: desktopCollapsed ? 'none' : 'block' }}>
          <Typography
            variant="h4"
            sx={{ fontWeight: 900, lineHeight: 1, color: 'primary.main' }}
          >
            coNDu
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Gestão condominial
          </Typography>
        </Box>
        {!isMobile && (
          <IconButton
            size="small"
            onClick={() => setDesktopCollapsed((prev) => !prev)}
            aria-label={desktopCollapsed ? 'Expandir menu lateral' : 'Recolher menu lateral'}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.06)}`
            }}
          >
            {desktopCollapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
          </IconButton>
        )}
      </Toolbar>

      <Divider />

      <Box sx={scrollAreaSx}>
        {visibleSections.map((section) => (
          <Box
            key={section.title}
            sx={{
              mb: 1.5,
              mx: desktopCollapsed ? 0.5 : 1,
              border: '1px solid',
              borderColor: alpha(theme.palette.primary.main, 0.14),
              borderRadius: 1,
              bgcolor: alpha(theme.palette.background.paper, 0.9),
              boxShadow: `0 12px 32px ${alpha(theme.palette.common.black, 0.05)}`,
              overflow: 'hidden'
            }}
          >
            <Box
              onClick={() => toggleSection(section.title)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: desktopCollapsed ? 1 : 2,
                py: 1.25,
                cursor: 'pointer',
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) }
              }}
            >
              <Typography
                variant="overline"
                sx={{
                  color: 'primary.main',
                  letterSpacing: 1,
                  fontWeight: 800,
                  display: desktopCollapsed ? 'none' : 'block'
                }}
              >
                {section.title}
              </Typography>
              <IconButton size="small" aria-label={`Alternar seção ${section.title}`}>
                {openSections[section.title] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            </Box>

            <Collapse in={openSections[section.title]} timeout="auto" unmountOnExit>
              <List sx={{ px: 1, py: 1 }}>
                {section.items.map(renderNavItem)}
              </List>
            </Collapse>
          </Box>
        ))}
      </Box>

      <Box sx={{ p: 2 }}>
        <Divider sx={{ mb: 1.5 }} />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: desktopCollapsed ? 'none' : 'block', mb: 1 }}
        >
          {getUserFirstName(user?.email)}
        </Typography>
          <List dense disablePadding>
          <Tooltip
            title={desktopCollapsed ? 'Alterar senha' : ''}
            placement="right"
            disableHoverListener={!desktopCollapsed}
            disableFocusListener={!desktopCollapsed}
            disableTouchListener={!desktopCollapsed}
          >
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                component={Link}
                to="/change-password"
                onClick={isMobile ? onMobileToggle : undefined}
                sx={{
                  ...itemButtonSx,
                  justifyContent: desktopCollapsed ? 'center' : 'flex-start'
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: desktopCollapsed ? 0 : 36,
                    mr: desktopCollapsed ? 0 : 1,
                    color: 'inherit'
              }}
            >
              <VpnKeyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Alterar senha"
              sx={{ display: desktopCollapsed ? 'none' : 'block' }}
            />
              </ListItemButton>
            </ListItem>
          </Tooltip>
          <Tooltip
            title={desktopCollapsed ? 'Sair do sistema' : ''}
            placement="right"
            disableHoverListener={!desktopCollapsed}
            disableFocusListener={!desktopCollapsed}
            disableTouchListener={!desktopCollapsed}
          >
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  logout();
                  if (isMobile) {
                    onMobileToggle();
                  }
                }}
                sx={{
                  ...itemButtonSx,
                  justifyContent: desktopCollapsed ? 'center' : 'flex-start'
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: desktopCollapsed ? 0 : 36,
                    mr: desktopCollapsed ? 0 : 1,
                    color: 'inherit'
              }}
            >
              <SupervisorAccountIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Sair do sistema"
              sx={{ display: desktopCollapsed ? 'none' : 'block' }}
            />
              </ListItemButton>
            </ListItem>
          </Tooltip>
        </List>
      </Box>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { md: drawerWidthValue }, flexShrink: { md: 0 } }}
      aria-label="menu lateral"
    >
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth }
        }}
      >
        {drawerContent}
      </Drawer>
      <Drawer
        variant="permanent"
        open
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidthValue,
            borderRight: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper'
          }
        }}
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
};

export default SidebarMenu;
